import React, { useState, ReactNode, useRef, useEffect } from "react";
import { useSwipeable, SwipeableHandlers } from "react-swipeable";

interface Tab {
  label: string;
  component: ReactNode;
}

interface SwitcherProps {
  tabs: Tab[];
  onTabChange?: (index: number) => void; // Optional callback prop to notify parent of tab change
}

const Switcher: React.FC<SwitcherProps> = ({ tabs, onTabChange }) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const underlineRef = useRef<HTMLDivElement>(null);

  const handleTabClick = (index: number) => {
    if (activeTabIndex !== index) {
      setActiveTabIndex(index);
      if (onTabChange) {
        onTabChange(index); // Notify parent of tab change
      }
    }
  };

  const handlers: SwipeableHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (activeTabIndex < tabs.length - 1) {
        const newIndex = activeTabIndex + 1;
        setActiveTabIndex(newIndex);
        if (onTabChange) {
          onTabChange(newIndex); // Notify parent of swipe change
        }
      }
    },
    onSwipedRight: () => {
      if (activeTabIndex > 0) {
        const newIndex = activeTabIndex - 1;
        setActiveTabIndex(newIndex);
        if (onTabChange) {
          onTabChange(newIndex); // Notify parent of swipe change
        }
      }
    },
    trackTouch: true,
    trackMouse: true,
    delta: 10,
  });

  // Update the position of the blue underline when activeTabIndex changes
  useEffect(() => {
    if (underlineRef.current) {
      underlineRef.current.style.transform = `translateX(${
        activeTabIndex * 100
      }%)`;
    }
  }, [activeTabIndex, tabs.length]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex relative py-3 bg-gradient-radial-tab flex-shrink-0">
        <div className="flex flex-1">
          {tabs.map((tab, index) => (
            <div key={tab.label} className="flex-1 flex justify-center">
              <span
                className={`cursor-pointer text-[12px] font-medium transition-colors duration-300 ${
                  activeTabIndex === index
                    ? "text-text-tab-active"
                    : "text-text-subtle"
                }`}
                onClick={() => handleTabClick(index)}
                aria-selected={activeTabIndex === index}
                role="tab"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleTabClick(index);
                  }
                }}
              >
                {tab.label.toLocaleUpperCase()}
              </span>
            </div>
          ))}
        </div>
        {/* Base Gray Underline */}
        <div className="absolute bottom-0 left-0 h-[1px] w-full bg-[#ECECEC]"></div>
        {/* Sliding Blue Underline */}
        <div
          ref={underlineRef}
          className="absolute bottom-0 h-[2px] bg-border-tab-active transition-transform duration-300 ease-in-out"
          style={{
            width: `${100 / tabs.length}%`,
            transform: `translateX(${activeTabIndex * 100}%)`,
            zIndex: 10, // Ensure the blue underline is above the gray one
          }}
        ></div>
      </div>
      <div
        className="flex-1 bg-bg-page-body overflow-y-auto relative"
        {...handlers}
      >
        {tabs[activeTabIndex] && tabs[activeTabIndex].component}
      </div>
    </div>
  );
};

export default Switcher;
